const integer = new Intl.NumberFormat('en-GB');

export const formatCount = (value: number) => integer.format(value);

/** "1 payment", "2 payments": the count and its noun, agreeing. */
export const countOf = (value: number, one: string, many = `${one}s`) =>
  `${formatCount(value)} ${value === 1 ? one : many}`;

export function formatPercent(value: number | null, digits = 0): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/** A reply time a person would say out loud: "25 sec", "2 min", "3 h 20 min". */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} sec`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  return `${Math.round(hours / 24)} days`;
}

export type Trend = 'up' | 'down' | 'flat';

/**
 * The change against the previous window, phrased for the card footer. A
 * percentage of a tiny base is noise, so small numbers read as a plain
 * difference instead.
 */
export function describeChange(
  value: number,
  previous: number
): { trend: Trend; label: string } {
  const diff = value - previous;
  if (diff === 0) return { trend: 'flat', label: 'Same as before' };
  const trend: Trend = diff > 0 ? 'up' : 'down';
  if (previous < 10) {
    return {
      trend,
      label: `${diff > 0 ? '+' : '−'}${formatCount(Math.abs(diff))}`,
    };
  }
  const percent = Math.round((Math.abs(diff) / previous) * 100);
  return { trend, label: `${diff > 0 ? '+' : '−'}${percent}%` };
}

/** Change in a rate, in percentage points. */
export function describeRateChange(
  value: number | null,
  previous: number | null
): { trend: Trend; label: string } | null {
  if (value === null || previous === null) return null;
  const points = Math.round((value - previous) * 1000) / 10;
  if (points === 0) return { trend: 'flat', label: 'Same as before' };
  return {
    trend: points > 0 ? 'up' : 'down',
    label: `${points > 0 ? '+' : '−'}${Math.abs(points)} pts`,
  };
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
